import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// i18next
import i18n from 'i18n'
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { getResult } from 'store/player/playerAsync';
import { setQuiz } from 'store/quiz/quizSlice';
import { selectResult } from 'store/player/playerSelectors';
import { selectQuiz } from 'store/quiz/quizSelectors';
import IQuiz from 'models/Quiz';
import QuizTypes from 'types/QuizTypes';
// MUI
import { Box } from '@mui/material';
// Components
import AppLoader from 'components/AppLoader';
import AppBar from './AppBar';
// Utilities
import { decodeBase64, getSearchParam } from 'utilities/Utilities';
import ResultsList from './ResultsList';
import TeamResultsList from './TeamResultsList';
import Prizes from './Prizes';
import QrCode from './QrCode';
import { NUMBER_OF_PLAYERS, REFRESH_INTERVAL_MS } from './constants';
import useCompactLayout from 'hooks/useCompactLayout';
import classes from './styles.module.css';

let interval: any;
const limit = NUMBER_OF_PLAYERS;

const LiveLeaderboardPage:FC = () => {
  const { slug } = useParams<{ slug:string }>();
  const { groupSlug } = useParams<{ groupSlug:string }>();

  const { search } = window.location;
  const hasHash = getSearchParam(search, 'hasHash');
  const scope = getSearchParam(search, 'scope') === 'team' ? 'team' : null;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const storeQuiz = useAppSelector(selectQuiz);

  const onReceiveMessage = (event:any) => {
    const { action, quizHash } = event.data;
    if ( action === 'preview' && quizHash ){
      dispatch(setQuiz(decodeBase64(quizHash)))
    }
  }

  useEffect(() => {
    window.addEventListener('message', onReceiveMessage, false);

    return () => {
      window.removeEventListener('message', onReceiveMessage, false);
    }
    // eslint-disable-next-line
  }, []);

  const result:any = useAppSelector(selectResult);
  const quiz:IQuiz | null = storeQuiz || result?.quiz || null;
  const quizGroup:any = result?.quizGroup || null;
  const [loading, setLoading] = useState<boolean>(false);

  const fetchResult = (isTimer: boolean) => {
    if (groupSlug) {
      const data:any = { quizGroupName: groupSlug, limit };
      if (hasHash) data['liveLeaderboardPreview'] = true;
      if (scope) data['scope'] = scope;
      dispatch(getResult(data)).unwrap()
        .catch(() => {
          if (!isTimer) {
            navigate('/404-not-found');
          }
        })
        .finally(() => setLoading(false));
    }
    if (slug) {
      const data:any = { quizName: slug, limit };
      if (hasHash) data['liveLeaderboardPreview'] = true;
      if (scope) data['scope'] = scope;
      dispatch(getResult(data)).unwrap()
      .catch(() => {
        if (!isTimer) {
          navigate('/404-not-found');
        }
      })
      .finally(() => setLoading(false));
    }
  }

  useEffect(() => {
    setLoading(true);
    fetchResult(false);

    if (!hasHash) {
      interval = setInterval(() => { fetchResult(true) }, REFRESH_INTERVAL_MS);
    }

    const rootDiv = document.getElementById('root');
    rootDiv?.classList.add('liveLeaderboardMode');

    return () => {
      if (interval) clearInterval(interval);
      rootDiv?.classList.remove('liveLeaderboardMode');
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!quiz) return;
    if (![QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quiz.type)) navigate('/404-not-found');
    if (quiz.type === QuizTypes.Prediction && quiz.numberOfQuestions === quiz.numberOfAnsweredQuestions) {
      clearInterval(interval);
    }
    document.documentElement.style.setProperty('--primaryColor', quiz.theme.primaryColor);
    i18n.changeLanguage(quiz.language || 'en');
    // eslint-disable-next-line
  }, [quiz]);

  useEffect(() => {
    if (!quizGroup) return;
    document.documentElement.style.setProperty('--primaryColor', quizGroup.theme.primaryColor);
  }, [quizGroup]);

  const bgImageUrl = quiz ? quiz.publicLeaderboard.wallpaperImage : quizGroup ? quizGroup.publicLeaderboard.wallpaperImage : '';
  const portraitBgImageUrl = quiz ? quiz.publicLeaderboard.portraitWallpaperImage : quizGroup ? quizGroup.publicLeaderboard.portraitWallpaperImage : '';

  const textColor = quiz? quiz.publicLeaderboard.textColor : quizGroup ? quizGroup.publicLeaderboard.textColor : '#fff';

  const isCompact = useCompactLayout();

  // On compact (stadium/short) screens the leaderboard and QR code can't fit on screen
  // together and stay readable, so they take turns as full-screen slides instead:
  // the top 10 results, then one combined QR+prizes slide, on the same interval the
  // results already rotate on. With no results at all there's no leaderboard slide -
  // just the QR+prizes slide, permanently.
  const qrEnabled = !!quiz?.publicLeaderboard.quizQRCodeEnabled;
  const activeResultsCount = scope === 'team' ? (result?.teamResults?.length || 0) : (result?.results?.length || 0);
  const resultsPageCount = activeResultsCount > 0 ? 1 : 0;
  const totalSlides = resultsPageCount + 1;

  const [slideIndex, setSlideIndex] = useState<number>(0);

  useEffect(() => {
    if (!isCompact || totalSlides <= 1) return;
    const slideInterval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % totalSlides);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(slideInterval);
  }, [isCompact, totalSlides]);

  useEffect(() => {
    if (slideIndex >= totalSlides) setSlideIndex(0);
  }, [slideIndex, totalSlides]);

  const isQrPrizesSlide = isCompact && slideIndex === resultsPageCount;

  if (loading || !result) return <AppLoader />;
  return (
    <Box
      className={`
        h-full flex flex-col bg-cover bg-center bg-black xl:bg-right-top
        ${isCompact ? 'overflow-hidden px-12 py-4 gap-3' : 'overflow-scroll px-4 py-2 sm:px-8 sm:py-4'}
      `}
      sx={{
        color: textColor,
        '@media screen and (orientation: portrait)': {
          backgroundImage: `url(${portraitBgImageUrl || bgImageUrl})`,
        },
        '@media screen and (orientation: landscape)': {
          backgroundImage: `url(${bgImageUrl || portraitBgImageUrl})`,
        }
      }}
    >
      <AppBar hideTitle={isQrPrizesSlide} />
      {isCompact ? (
        <div className="flex-1 min-h-0 flex">
          {isQrPrizesSlide ? (
            <div key="qr-prizes" className={`flex-1 min-w-0 min-h-0 flex flex-row gap-2 overflow-hidden ${classes.pageFade}`}>
              {qrEnabled && (
                <div className="flex-1 min-w-0 min-h-0 flex">
                  <QrCode />
                </div>
              )}
              <div className="flex-1 min-w-0 min-h-0 flex flex-col justify-center overflow-hidden">
                <Prizes />
              </div>
            </div>
          ) : (
            <div key={`results-${slideIndex}`} className={`flex-1 min-w-0 min-h-0 flex flex-col justify-center overflow-hidden ${classes.pageFade}`}>
              {scope === 'team' ? <TeamResultsList page={slideIndex} /> : <ResultsList page={slideIndex} />}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col xl:flex-row portrait:flex-col gap-4 sm:gap-12">
          <div className="flex-1 px-4 sm:px-8">
            {scope === 'team' ? <TeamResultsList /> : <ResultsList />}
            <Prizes />
          </div>
          <div className="flex-1 px-4 sm:px-8">
            <QrCode />
          </div>
        </div>
      )}
    </Box>
  );
}

export default LiveLeaderboardPage;
